/**
 * LibraryPage — Bibliothèque de parties
 *
 * MasterDetail layout: sidebar with filters / main game list.
 * Supports own games, public shared games, private shared games,
 * tag filtering with color codes, text search, tag editing,
 * auto-refresh, and contextual actions per game type.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import type { Session, LibrarySection, LibraryOpenMode, LibraryNavigationRequest } from '../types/app';
import type {
  LibraryGameSummary,
  LibrarySharedGameSummary,
  LibraryShareInfo,
  LibraryOriginType,
} from '../types/library';
import { Contract } from '../types/game';
import { PREDEFINED_TAGS, AUTO_TAGS } from '../types/library';
import ShareGameModal from '../components/library/ShareGameModal';
import ShareLinkModal from '../components/library/ShareLinkModal';
import PollModal from '../components/library/PollModal';
import PollResultsPreview from '../components/library/PollResultsPreview';
import MasterDetail from '../components/MasterDetail';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { useApp } from '../contexts/AppContext';
import { useTabReselect } from '../hooks/useTabReselect';
import {
  FolderIcon, GlobeIcon, LockIcon, SearchIcon,
  GameCompleteIcon, GamePartialIcon, PollIcon,
  EyeIcon, ReplayIcon, PlayIcon, ContinueIcon,
  ShareIcon, TrashIcon, TagIcon, EditIcon, LibraryIcon, GroupIcon, LinkIcon,
} from '../components/Icons';
import '../styles/library.css';
import { BACKEND_URL } from '../config';
import {
  syncLibraryUrl,
} from '../utils/libraryDeepLink';
import {
  buildLibraryHistoryContext,
  buildDiffereNavigationFromLibrary,
  canOpenDiffereFromLibrary,
  formatLibraryDiffereCardTitle,
} from '../utils/libraryOrigin';

const REFRESH_INTERVAL_MS = 30_000;
const PAGE_SIZE = 30;

type SortField = 'date' | 'contract' | 'score';

const SORT_LABELS: Record<SortField, string> = {
  date: 'Date',
  contract: 'Contrat',
  score: 'Score',
};

type Section = LibrarySection;

const CONTRACT_LABELS: Record<number, string> = {
  [Contract.Pass]: 'Passe',
  [Contract.Petite]: 'Petite',
  [Contract.Garde]: 'Garde',
  [Contract.GardeSans]: 'G. Sans',
  [Contract.GardeContre]: 'G. Contre',
};

const ORIGIN_LABELS: Record<LibraryOriginType, string> = {
  libre: 'Libre',
  differe_indiv: 'Diff. indiv.',
  differe_tq: 'Diff. TQ',
  defi: 'Défi',
  training: 'Entraînement',
};

const SECTION_LABELS: Record<Section, string> = {
  own: 'Mes parties',
  my_shares: 'Mes partages',
  shared_public: 'Publiques',
  shared_private: 'Partagées avec moi',
};

interface LibraryPageProps {
  session: Session;
  isAdmin?: boolean;
}

export default function LibraryPage({ session, isAdmin }: LibraryPageProps) {
  const {
    startTraining,
    activeTab,
    libraryNavigationRequest,
    clearLibraryNavigationRequest,
    navigateToDiffere,
    setLibraryNewCount,
    libraryPendingPollCount,
  } = useApp();
  const { isCompact } = useResponsive();

  const [section, setSection] = useState<Section>('own');
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  useTabReselect('library', () => setShowMobileDetail(false));
  const [ownGames, setOwnGames] = useState<LibraryGameSummary[]>([]);
  const [sharedPublicGames, setSharedPublicGames] = useState<LibrarySharedGameSummary[]>([]);
  const [sharedPrivateGames, setSharedPrivateGames] = useState<LibrarySharedGameSummary[]>([]);
  const [myShares, setMyShares] = useState<LibraryShareInfo[]>([]);
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [shareGame, setShareGame] = useState<{ id: number; tags: string[]; note?: string } | null>(null);
  const [shareLinkModal, setShareLinkModal] = useState<{
    id: number;
    shareType: 'public' | 'private';
    allowReshare: boolean;
    isOwner: boolean;
  } | null>(null);
  const [editingTagsGameId, setEditingTagsGameId] = useState<number | null>(null);
  const [editingNoteGameId, setEditingNoteGameId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortField>('date');
  const [pollResultsGame, setPollResultsGame] = useState<LibrarySharedGameSummary | null>(null);
  const [pendingPollsOnly, setPendingPollsOnly] = useState(false);
  const [highlightedGameId, setHighlightedGameId] = useState<number | null>(null);
  const [pendingDeepLink, setPendingDeepLink] = useState<{
    sharedGameId: number;
    mode: LibraryOpenMode;
  } | null>(null);

  const pseudo = session.pseudo;

  const refreshLibraryBadge = useCallback(async () => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/notifications?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (res.ok) {
        const data = await res.json() as { newShareCount: number };
        setLibraryNewCount(data.newShareCount ?? 0);
      }
    } catch { /* ignore */ }
  }, [pseudo, setLibraryNewCount]);

  const clearShareNewLocally = useCallback((sharedGameId: number) => {
    const mark = <T extends { id: number; is_new?: boolean }>(prev: T[]) =>
      prev.map(g => (g.id === sharedGameId ? { ...g, is_new: false } : g));
    setSharedPublicGames(mark);
    setSharedPrivateGames(mark);
  }, []);

  const markShareViewed = useCallback(async (sharedGameId: number) => {
    clearShareNewLocally(sharedGameId);
    try {
      await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}/view?pseudo=${encodeURIComponent(pseudo)}`,
        { method: 'POST' },
      );
      await refreshLibraryBadge();
    } catch { /* ignore */ }
  }, [pseudo, clearShareNewLocally, refreshLibraryBadge]);

  const handleCopyShareLink = useCallback((game: {
    id: number;
    share_type: 'public' | 'private' | string;
    allow_reshare?: boolean;
  }, isOwner = false) => {
    setShareLinkModal({
      id: game.id,
      shareType: game.share_type === 'public' ? 'public' : 'private',
      allowReshare: game.allow_reshare ?? game.share_type === 'public',
      isOwner,
    });
  }, []);

  // ============================================
  // Data fetching
  // ============================================

  const fetchTagCounts = useCallback(async () => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/tag-counts?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (res.ok) {
        const data = await res.json();
        const counts: Record<string, number> = {};
        for (const row of data.tags) {
          counts[row.tag_name] = row.count;
        }
        setTagCounts(counts);
      }
    } catch { /* ignore */ }
  }, [pseudo]);

  const fetchCustomTags = useCallback(async () => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/tags?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (res.ok) {
        const data = await res.json();
        setCustomTags(data.custom ?? []);
      }
    } catch { /* ignore */ }
  }, [pseudo]);

  const fetchGames = useCallback(async (reset: boolean) => {
    if (section === 'my_shares') return;

    const currentLength = reset
      ? 0
      : section === 'own'
        ? ownGames.length
        : section === 'shared_public'
          ? sharedPublicGames.length
          : sharedPrivateGames.length;

    if (!reset && (!hasMore || loadingMore || loading)) return;

    if (reset) {
      if (currentLength === 0) setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({
        pseudo,
        section,
        limit: String(PAGE_SIZE),
        offset: String(currentLength),
        sort: sortBy,
      });
      if (activeTags.length > 0) params.set('tags', activeTags.join(','));
      if (searchText.trim()) params.set('search', searchText.trim());

      const res = await fetch(`${BACKEND_URL}/api/library/games?${params}`);
      if (res.ok) {
        const data = await res.json();
        const games = data.games ?? [];
        setHasMore(data.has_more ?? games.length === PAGE_SIZE);
        if (section === 'own') {
          setOwnGames(prev => (reset ? games : [...prev, ...games]));
        } else if (section === 'shared_public') {
          setSharedPublicGames(prev => (reset ? games : [...prev, ...games]));
        } else {
          setSharedPrivateGames(prev => (reset ? games : [...prev, ...games]));
        }
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [
    pseudo,
    section,
    activeTags,
    searchText,
    sortBy,
    hasMore,
    loadingMore,
    loading,
    ownGames.length,
    sharedPublicGames.length,
    sharedPrivateGames.length,
  ]);

  const fetchMyShares = useCallback(async (reset: boolean) => {
    const currentLength = reset ? 0 : myShares.length;
    if (!reset && (!hasMore || loadingMore || loading)) return;

    if (reset) {
      if (currentLength === 0) setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams({
        pseudo,
        limit: String(PAGE_SIZE),
        offset: String(currentLength),
        sort: sortBy,
      });
      const res = await fetch(`${BACKEND_URL}/api/library/my-shares?${params}`);
      if (res.ok) {
        const data = await res.json();
        const shares = data.shares ?? [];
        setMyShares(prev => (reset ? shares : [...prev, ...shares]));
        setHasMore(data.has_more ?? shares.length === PAGE_SIZE);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pseudo, sortBy, hasMore, loadingMore, loading, myShares.length]);

  const loadSection = useCallback(async (reset: boolean) => {
    if (section === 'my_shares') {
      await fetchMyShares(reset);
    } else {
      await fetchGames(reset);
    }
  }, [section, fetchMyShares, fetchGames]);

  const refreshSection = useCallback(async () => {
    await loadSection(true);
  }, [loadSection]);

  const loadMore = useCallback(() => {
    void loadSection(false);
  }, [loadSection]);

  useEffect(() => {
    setHasMore(false);
    void loadSection(true);
  }, [section, activeTags, searchText, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTagCounts(); fetchCustomTags(); }, [fetchTagCounts, fetchCustomTags]);

  // Auto-refresh every 30s
  useEffect(() => {
    const id = setInterval(() => { void loadSection(true); }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [loadSection]);

  const handlePullRefresh = useCallback(async () => {
    await fetchTagCounts();
    await loadSection(true);
  }, [fetchTagCounts, loadSection]);

  const { pullDistance, isRefreshing, isPulling } = usePullToRefresh({
    enabled: isCompact,
    onRefresh: handlePullRefresh,
    scrollRef: listRef,
  });

  const sentinelRef = useInfiniteScroll({
    enabled: hasMore,
    hasMore,
    loading,
    loadingMore,
    onLoadMore: loadMore,
    scrollRef: listRef,
  });

  // Refresh when tab becomes visible after a save from training
  useEffect(() => {
    if (activeTab !== 'library') return;
    const needsRefresh = sessionStorage.getItem('library_needs_refresh');
    if (needsRefresh) {
      sessionStorage.removeItem('library_needs_refresh');
      void refreshSection();
      fetchTagCounts();
    }
  }, [activeTab, refreshSection, fetchTagCounts]);

  useEffect(() => {
    if (activeTab !== 'library') setShowMobileDetail(false);
  }, [activeTab]);

  // ============================================
  // Navigation (compact)
  // ============================================

  const handleSectionClick = useCallback((next: Section) => {
    if (next !== section && next !== 'shared_public' && next !== 'shared_private') {
      setPendingPollsOnly(false);
    }
    setSection(next);
    syncLibraryUrl({
      section: next,
      sharedGameId: null,
      mode: null,
      pendingPollsOnly: next === 'shared_public' || next === 'shared_private' ? pendingPollsOnly : false,
    });
    if (isCompact) setShowMobileDetail(true);
  }, [isCompact, section, pendingPollsOnly]);

  const handleBrowseGames = useCallback(() => {
    setShowMobileDetail(true);
  }, []);

  // ============================================
  // Tag toggle
  // ============================================

  const toggleTag = useCallback((tag: string) => {
    setActiveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag],
    );
    if (isCompact) setShowMobileDetail(true);
  }, [isCompact]);

  const allTags = useMemo(() => {
    const tags = [...PREDEFINED_TAGS as unknown as string[]];
    for (const ct of customTags) { if (!tags.includes(ct)) tags.push(ct); }
    const withCounts = tags.map(tag => ({ name: tag, count: tagCounts[tag] ?? 0 }));
    for (const [tag, count] of Object.entries(tagCounts)) {
      if (!tags.includes(tag)) withCounts.push({ name: tag, count });
    }
    return withCounts;
  }, [customTags, tagCounts]);

  // Shared game tags for filtering
  const sharedTagsList = useMemo(() => {
    const games = section === 'shared_public' ? sharedPublicGames : sharedPrivateGames;
    const counts: Record<string, number> = {};
    for (const g of games) {
      for (const t of g.tags ?? []) {
        counts[t] = (counts[t] ?? 0) + 1;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [section, sharedPublicGames, sharedPrivateGames]);

  // ============================================
  // Actions
  // ============================================

  const handleDelete = useCallback(async (gameId: number) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}?pseudo=${encodeURIComponent(pseudo)}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        setOwnGames(prev => prev.filter(g => g.id !== gameId));
        fetchTagCounts();
      }
    } catch { /* ignore */ }
  }, [pseudo, fetchTagCounts]);

  const handleOpenGame = useCallback(async (gameId: number, mode: 'replay' | 'play') => {
    syncLibraryUrl({ sharedGameId: null, mode: null });
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (!res.ok) return;
      const game = await res.json();

      sessionStorage.setItem('library_load_request', JSON.stringify({
        libraryGameId: game.id,
        libraryGameSource: 'own',
        commentsEnabled: game.comments_enabled ?? true,
        gameData: game.game_data,
        mode,
        position: game.recommended_position ?? 0,
        biddingMode: mode === 'play' ? 'auto' : undefined,
        isPartial: game.is_partial,
        partialPhase: game.partial_phase,
        partialTrickNum: game.partial_trick_num,
        historyContext: buildLibraryHistoryContext(game, 'own'),
      }));

      startTraining({
        sessionId: '',
        displayMode: 'classic',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch { /* ignore */ }
  }, [pseudo, startTraining]);

  const handleOpenSharedGame = useCallback(async (sharedGameId: number, mode: 'replay' | 'play') => {
    syncLibraryUrl({ sharedGameId: null, mode: null });
    void markShareViewed(sharedGameId);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (!res.ok) return;
      const sg = await res.json();

      sessionStorage.setItem('library_load_request', JSON.stringify({
        libraryGameId: sg.id,
        libraryGameSource: 'shared',
        commentsEnabled: sg.comments_enabled ?? true,
        gameData: sg.game_data,
        mode,
        position: sg.recommended_position ?? 0,
        biddingMode: mode === 'play' ? 'auto' : undefined,
        isPartial: sg.is_partial,
        partialPhase: sg.partial_phase,
        partialTrickNum: sg.partial_trick_num,
        historyContext: buildLibraryHistoryContext(sg, 'shared'),
      }));

      startTraining({
        sessionId: '',
        displayMode: 'classic',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch { /* ignore */ }
  }, [pseudo, startTraining, markShareViewed]);

  const handleOpenDiffereSource = useCallback((game: LibraryGameSummary | LibrarySharedGameSummary) => {
    const req = buildDiffereNavigationFromLibrary(game);
    if (!req) return;
    navigateToDiffere(req);
  }, [navigateToDiffere]);

  const applyLibraryNavigation = useCallback(async (req: LibraryNavigationRequest) => {
    let targetSection = req.section;
    if (!targetSection && req.sharedGameId) {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/library/shared/${req.sharedGameId}?pseudo=${encodeURIComponent(pseudo)}`,
        );
        if (res.ok) {
          const sg = await res.json() as { share_type?: string; shared_by?: string };
          if (sg.shared_by?.toLowerCase() === pseudo.toLowerCase()) {
            targetSection = 'my_shares';
          } else {
            targetSection = sg.share_type === 'public' ? 'shared_public' : 'shared_private';
          }
        }
      } catch { /* ignore */ }
    }
    const resolvedSection = targetSection ?? 'shared_private';
    setSection(resolvedSection);
    setPendingPollsOnly(!!req.pendingPollsOnly);
    if (isCompact) setShowMobileDetail(true);

    syncLibraryUrl({
      section: resolvedSection,
      sharedGameId: req.sharedGameId ?? null,
      mode: req.sharedGameId && !req.highlightOnly ? (req.mode ?? 'replay') : null,
      pendingPollsOnly: !!req.pendingPollsOnly,
      highlightOnly: !!req.highlightOnly,
    });

    if (req.sharedGameId) {
      if (req.highlightOnly) {
        setHighlightedGameId(req.sharedGameId);
      } else {
        setPendingDeepLink({
          sharedGameId: req.sharedGameId,
          mode: req.mode ?? 'replay',
        });
      }
    } else if (req.ownGameId) {
      setSection('own');
      if (req.highlightOnly) {
        setHighlightedGameId(req.ownGameId);
      }
    }
  }, [pseudo, isCompact]);

  useEffect(() => {
    if (activeTab !== 'library' || !libraryNavigationRequest) return;
    const req = libraryNavigationRequest;
    clearLibraryNavigationRequest();
    void applyLibraryNavigation(req);
  }, [
    activeTab,
    libraryNavigationRequest,
    clearLibraryNavigationRequest,
    applyLibraryNavigation,
  ]);

  const handleOpenSharedGameFromStart = useCallback(async (sharedGameId: number) => {
    void markShareViewed(sharedGameId);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (!res.ok) return;
      const sg = await res.json();

      sessionStorage.setItem('library_load_request', JSON.stringify({
        libraryGameId: sg.id,
        libraryGameSource: 'shared',
        commentsEnabled: sg.comments_enabled ?? true,
        gameData: sg.game_data,
        mode: 'play',
        position: sg.recommended_position ?? 0,
        biddingMode: 'free',
        isPartial: false,
        historyContext: buildLibraryHistoryContext(sg, 'shared'),
      }));

      startTraining({
        sessionId: '',
        displayMode: 'classic',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch { /* ignore */ }
  }, [pseudo, startTraining, markShareViewed]);

  const handlePollVote = useCallback(async (game: LibrarySharedGameSummary) => {
    void markShareViewed(game.id);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${game.id}?pseudo=${encodeURIComponent(pseudo)}`,
      );
      if (!res.ok) return;
      const sg = await res.json();

      sessionStorage.setItem('library_poll_game', JSON.stringify({
        sharedGameId: game.id,
        sharedBy: game.shared_by,
        gameData: sg.game_data,
        position: sg.recommended_position ?? 0,
        isPartial: sg.is_partial,
        partialPhase: sg.partial_phase,
        partialTrickNum: sg.partial_trick_num,
      }));

      sessionStorage.setItem('library_load_request', JSON.stringify({
        libraryGameId: sg.id,
        libraryGameSource: 'shared',
        commentsEnabled: sg.comments_enabled ?? true,
        gameData: sg.game_data,
        mode: 'play',
        position: sg.recommended_position ?? 0,
        biddingMode: 'auto',
        isPartial: sg.is_partial,
        partialPhase: sg.partial_phase,
        partialTrickNum: sg.partial_trick_num,
        pollVoteMode: true,
        historyContext: {
          kind: 'poll' as const,
          poll: {
            sharedGameId: sg.id,
            sharedBy: sg.shared_by,
          },
          library: buildLibraryHistoryContext(sg, 'shared').library,
        },
      }));

      startTraining({
        sessionId: '',
        displayMode: 'classic',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch { /* ignore */ }
  }, [pseudo, startTraining, markShareViewed]);

  const shareToSummary = useCallback((share: LibraryShareInfo): LibrarySharedGameSummary => ({
    id: share.id,
    shared_by: pseudo,
    origin_type: share.origin_type,
    contract: share.contract,
    player_count: share.player_count,
    is_partial: share.is_partial,
    is_poll: share.is_poll,
    share_type: share.share_type,
    recommended_position: share.recommended_position ?? 0,
    partial_phase: share.partial_phase,
    partial_trick_num: share.partial_trick_num,
    shared_at: share.shared_at,
    tags: share.tags,
    poll_response_count: share.poll_response_count,
    user_has_voted: false,
  }), [pseudo]);

  useEffect(() => {
    if (!pendingDeepLink || loading) return;
    const { sharedGameId, mode } = pendingDeepLink;

    if (section === 'my_shares') {
      const share = myShares.find(s => s.id === sharedGameId);
      if (!share) return;

      setHighlightedGameId(sharedGameId);
      setPendingDeepLink(null);

      if (mode === 'poll') {
        void handlePollVote(shareToSummary(share));
      } else {
        void handleOpenSharedGame(sharedGameId, mode);
      }
      return;
    }

    const allShared = [...sharedPublicGames, ...sharedPrivateGames];
    const game = allShared.find(g => g.id === sharedGameId);
    if (!game) return;

    setHighlightedGameId(game.id);
    setPendingDeepLink(null);

    if (mode === 'poll') {
      void handlePollVote(game);
    } else {
      void handleOpenSharedGame(sharedGameId, mode);
    }
  }, [
    pendingDeepLink,
    loading,
    section,
    myShares,
    sharedPublicGames,
    sharedPrivateGames,
    handlePollVote,
    handleOpenSharedGame,
    shareToSummary,
  ]);

  // Tag update for own games
  const handleUpdateTags = useCallback(async (gameId: number, tags: string[]) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}?pseudo=${encodeURIComponent(pseudo)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags }),
        },
      );
      if (res.ok) {
        setOwnGames(prev => prev.map(g => g.id === gameId ? { ...g, tags } : g));
        fetchTagCounts();
        setEditingTagsGameId(null);
      }
    } catch { /* ignore */ }
  }, [pseudo, fetchTagCounts]);

  const handleUpdateNote = useCallback(async (gameId: number, note: string) => {
    const trimmed = note.trim();
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/games/${gameId}?pseudo=${encodeURIComponent(pseudo)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: trimmed || null }),
        },
      );
      if (res.ok) {
        setOwnGames(prev => prev.map(g => (
          g.id === gameId ? { ...g, note: trimmed || undefined } : g
        )));
        setEditingNoteGameId(null);
      }
    } catch { /* ignore */ }
  }, [pseudo]);

  // Admin: delete shared game
  const handleAdminDeleteShared = useCallback(async (sharedGameId: number) => {
    if (!confirm('Supprimer cette partie partagée ?')) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/admin/shared/${sharedGameId}?pseudo=${encodeURIComponent(pseudo)}`,
        { method: 'DELETE' },
      );
      if (res.ok) void loadSection(true);
    } catch { /* ignore */ }
  }, [pseudo, loadSection]);

  // Admin: update shared game
  const handleAdminUpdateShared = useCallback(async (
    sharedGameId: number,
    updates: { tags?: string[]; is_poll?: boolean; share_comment?: string | null },
  ) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/admin/shared/${sharedGameId}?pseudo=${encodeURIComponent(pseudo)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        },
      );
      if (res.ok) void loadSection(true);
    } catch { /* ignore */ }
  }, [pseudo, loadSection]);

  const handleCancelShare = useCallback(async (sharedGameId: number) => {
    if (!confirm('Annuler ce partage ? Les destinataires n\'y auront plus accès.')) return;
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/library/shared/${sharedGameId}?pseudo=${encodeURIComponent(pseudo)}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        setMyShares(prev => prev.filter(s => s.id !== sharedGameId));
        fetchTagCounts();
      }
    } catch { /* ignore */ }
  }, [pseudo, fetchTagCounts]);

  // ============================================
  // Display helpers
  // ============================================

  const filteredMyShares = useMemo(() => {
    let list = myShares;
    if (activeTags.length > 0) {
      list = list.filter(s => activeTags.every(tag => s.tags?.includes(tag)));
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(s => {
        const origin = (ORIGIN_LABELS[s.origin_type] ?? s.origin_type).toLowerCase();
        const contract = (CONTRACT_LABELS[s.contract] ?? '').toLowerCase();
        const recipients = (s.recipients ?? []).map(r => r.id.toLowerCase()).join(' ');
        const visibility = s.share_type === 'public' ? 'publique' : 'privée';
        return origin.includes(q) || contract.includes(q) || recipients.includes(q) || visibility.includes(q);
      });
    }
    return list;
  }, [myShares, activeTags, searchText]);

  const mySharesTagsList = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of myShares) {
      for (const t of s.tags ?? []) {
        counts[t] = (counts[t] ?? 0) + 1;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [myShares]);

  const displaySharedGamesRaw = section === 'shared_public'
    ? sharedPublicGames
    : section === 'shared_private'
      ? sharedPrivateGames
      : [];
  const displaySharedGames = useMemo(() => {
    if (!pendingPollsOnly) return displaySharedGamesRaw;
    return displaySharedGamesRaw.filter(g => g.is_poll && !g.user_has_voted);
  }, [displaySharedGamesRaw, pendingPollsOnly]);

  useEffect(() => {
    if (!highlightedGameId || loading) return;
    const scrollTimer = window.setTimeout(() => {
      const el = section === 'own'
        ? document.querySelector(`[data-own-game-id="${highlightedGameId}"]`)
        : document.querySelector(`[data-shared-game-id="${highlightedGameId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const clearTimer = window.setTimeout(() => {
      setHighlightedGameId(null);
      syncLibraryUrl({ highlightOnly: false });
    }, 4000);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightedGameId, loading, displaySharedGames, section, filteredMyShares, ownGames]);

  const gameCount = section === 'own'
    ? ownGames.length
    : section === 'my_shares'
      ? filteredMyShares.length
      : displaySharedGames.length;
  const hasActiveFilters = activeTags.length > 0 || searchText.trim().length > 0 || pendingPollsOnly;

  const emptyMessage = pendingPollsOnly
    ? 'Aucun sondage en attente de votre vote'
    : hasActiveFilters
    ? (section === 'my_shares'
      ? 'Aucun partage trouvé avec ces critères'
      : 'Aucune partie trouvée avec ces critères')
    : section === 'own'
      ? 'Votre bibliothèque est vide'
      : section === 'my_shares'
        ? 'Vous n\'avez encore rien partagé'
        : 'Aucune partie partagée';

  const countLabel = section === 'my_shares'
    ? `${gameCount} partage${gameCount !== 1 ? 's' : ''}`
    : `${gameCount} partie${gameCount !== 1 ? 's' : ''}`;

  const detailTitle = isCompact && showMobileDetail
    ? `${SECTION_LABELS[section]}${loading ? '' : ` (${gameCount})`}`
    : SECTION_LABELS[section];

  // ============================================
  // Sidebar
  // ============================================

  const sidebar = (
    <div className="library-sidebar">
      <h2 className="library-sidebar-title">
        <LibraryIcon size={20} />
        Bibliothèque
      </h2>

      {/* Section selector */}
      <div className="library-sections">
        <button
          className={`library-section-btn ${section === 'own' ? 'active' : ''}`}
          onClick={() => handleSectionClick('own')}
        >
          <FolderIcon size={16} /> {SECTION_LABELS.own}
        </button>
        <button
          className={`library-section-btn ${section === 'my_shares' ? 'active' : ''}`}
          onClick={() => handleSectionClick('my_shares')}
        >
          <ShareIcon size={16} /> {SECTION_LABELS.my_shares}
        </button>
        <button
          className={`library-section-btn ${section === 'shared_public' ? 'active' : ''}`}
          onClick={() => handleSectionClick('shared_public')}
        >
          <GlobeIcon size={16} /> {SECTION_LABELS.shared_public}
        </button>
        <button
          className={`library-section-btn ${section === 'shared_private' ? 'active' : ''}`}
          onClick={() => handleSectionClick('shared_private')}
        >
          <LockIcon size={16} /> {SECTION_LABELS.shared_private}
        </button>
      </div>

      <hr className="library-sidebar-divider" />

      {/* Search */}
      <div className="library-search">
        <SearchIcon size={14} className="library-search-icon" />
        <input
          className="library-search-input"
          type="text"
          placeholder="Rechercher…"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>

      {(section === 'shared_public' || section === 'shared_private') && libraryPendingPollCount > 0 && (
        <button
          type="button"
          className={`library-poll-filter ${pendingPollsOnly ? 'active' : ''}`}
          onClick={() => {
            setPendingPollsOnly(v => {
              const next = !v;
              syncLibraryUrl({ section, pendingPollsOnly: next });
              return next;
            });
            if (isCompact) setShowMobileDetail(true);
          }}
        >
          <PollIcon size={14} />
          Sondages à voter
          <span className="library-tag-count">{libraryPendingPollCount}</span>
        </button>
      )}

      {/* Tags */}
      {section === 'own' && allTags.length > 0 && (
        <div>
          <div className="library-sidebar-section-title">Tags</div>
          {/* Predefined */}
          <div className="library-tags-group">
            {allTags
              .filter(t => (PREDEFINED_TAGS as unknown as string[]).includes(t.name))
              .map(tag => (
                <button
                  key={tag.name}
                  className={`library-tag ${activeTags.includes(tag.name) ? 'active' : ''}`}
                  data-tag={tag.name}
                  onClick={() => toggleTag(tag.name)}
                >
                  {tag.name}
                  {tag.count > 0 && <span className="library-tag-count">{tag.count}</span>}
                </button>
              ))}
          </div>
          {/* Auto tags */}
          {allTags.filter(t => (AUTO_TAGS as unknown as string[]).includes(t.name) && t.count > 0).length > 0 && (
            <div className="library-tags-group">
              {allTags
                .filter(t => (AUTO_TAGS as unknown as string[]).includes(t.name) && t.count > 0)
                .map(tag => (
                  <button
                    key={tag.name}
                    className={`library-tag ${activeTags.includes(tag.name) ? 'active' : ''}`}
                    data-tag={tag.name}
                    onClick={() => toggleTag(tag.name)}
                  >
                    {tag.name}
                    <span className="library-tag-count">{tag.count}</span>
                  </button>
                ))}
            </div>
          )}
          {/* Custom */}
          {allTags.filter(t =>
            !(PREDEFINED_TAGS as unknown as string[]).includes(t.name)
            && !(AUTO_TAGS as unknown as string[]).includes(t.name)
            && t.count > 0
          ).length > 0 && (
            <div className="library-tags-group">
              {allTags
                .filter(t =>
                  !(PREDEFINED_TAGS as unknown as string[]).includes(t.name)
                  && !(AUTO_TAGS as unknown as string[]).includes(t.name)
                  && t.count > 0
                )
                .map(tag => (
                  <button
                    key={tag.name}
                    className={`library-tag ${activeTags.includes(tag.name) ? 'active' : ''}`}
                    data-tag={tag.name}
                    onClick={() => toggleTag(tag.name)}
                  >
                    {tag.name}
                    <span className="library-tag-count">{tag.count}</span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Shared / my-shares tags */}
      {(section === 'shared_public' || section === 'shared_private') && sharedTagsList.length > 0 && (
        <div>
          <div className="library-sidebar-section-title">Tags</div>
          <div className="library-tags-group">
            {sharedTagsList.map(tag => (
              <button
                key={tag.name}
                className={`library-tag ${activeTags.includes(tag.name) ? 'active' : ''}`}
                data-tag={tag.name}
                onClick={() => toggleTag(tag.name)}
              >
                {tag.name}
                <span className="library-tag-count">{tag.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {section === 'my_shares' && mySharesTagsList.length > 0 && (
        <div>
          <div className="library-sidebar-section-title">Tags</div>
          <div className="library-tags-group">
            {mySharesTagsList.map(tag => (
              <button
                key={tag.name}
                className={`library-tag ${activeTags.includes(tag.name) ? 'active' : ''}`}
                data-tag={tag.name}
                onClick={() => toggleTag(tag.name)}
              >
                {tag.name}
                <span className="library-tag-count">{tag.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {isCompact && (
        <button type="button" className="library-sidebar-browse" onClick={handleBrowseGames}>
          {section === 'my_shares' ? 'Voir les partages' : 'Voir les parties'}
          {loading ? '…' : ` (${gameCount})`}
        </button>
      )}
    </div>
  );

  // ============================================
  // Main content
  // ============================================

  const mainContent = (
    <div className="library-main">
      <div className="library-main-header">
        <span className="library-main-title">{SECTION_LABELS[section]}</span>
        <div className="library-main-header-actions">
          <label className="library-sort">
            <span className="library-sort-label">Trier</span>
            <select
              className="library-sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortField)}
            >
              {(Object.keys(SORT_LABELS) as SortField[]).map(key => (
                <option key={key} value={key}>{SORT_LABELS[key]}</option>
              ))}
            </select>
          </label>
          <span className="library-main-count">
            {loading ? '...' : countLabel}
          </span>
        </div>
      </div>

      <div
        className={`library-list${isPulling ? ' library-list--pulling' : ''}`}
        ref={listRef}
      >
        {isCompact && (
          <div
            className={`library-pull-indicator${isRefreshing ? ' refreshing' : ''}`}
            style={{ height: isPulling ? Math.max(pullDistance, isRefreshing ? 48 : 0) : 0 }}
            aria-hidden={!isPulling}
          >
            {isRefreshing
              ? 'Actualisation…'
              : pullDistance >= 72
                ? 'Relâcher pour actualiser'
                : 'Tirer pour actualiser'}
          </div>
        )}

        {loading && gameCount === 0 && <div className="library-loading">Chargement…</div>}

        {!loading && gameCount === 0 && (
          <div className="library-empty">
            <LibraryIcon size={40} />
            <div>{emptyMessage}</div>
          </div>
        )}

        {/* Own games */}
        {section === 'own' && ownGames.map(game => (
          <OwnGameCard
            key={game.id}
            game={game}
            highlighted={highlightedGameId === game.id}
            customTags={customTags}
            editingTags={editingTagsGameId === game.id}
            editingNote={editingNoteGameId === game.id}
            onEditTags={() => {
              setEditingNoteGameId(null);
              setEditingTagsGameId(editingTagsGameId === game.id ? null : game.id);
            }}
            onEditNote={() => {
              setEditingTagsGameId(null);
              setEditingNoteGameId(editingNoteGameId === game.id ? null : game.id);
            }}
            onSaveTags={(tags) => handleUpdateTags(game.id, tags)}
            onSaveNote={(note) => handleUpdateNote(game.id, note)}
            onCancelEditTags={() => setEditingTagsGameId(null)}
            onCancelEditNote={() => setEditingNoteGameId(null)}
            onDelete={() => handleDelete(game.id)}
            onReview={() => handleOpenGame(game.id, 'replay')}
            onReplay={() => handleOpenGame(game.id, 'play')}
            onShare={() => setShareGame({ id: game.id, tags: game.tags, note: game.note })}
            onOpenDiffere={canOpenDiffereFromLibrary(game)
              ? () => handleOpenDiffereSource(game)
              : undefined}
          />
        ))}

        {/* My shares */}
        {section === 'my_shares' && filteredMyShares.map(share => (
          <MyShareCard
            key={share.id}
            share={share}
            pseudo={pseudo}
            highlighted={highlightedGameId === share.id}
            onReview={() => handleOpenSharedGame(share.id, 'replay')}
            onShowResults={share.is_poll
              ? () => setPollResultsGame(shareToSummary(share))
              : undefined}
            onCancel={() => handleCancelShare(share.id)}
            onCopyLink={share.allow_reshare !== false || share.share_type === 'public'
              ? () => handleCopyShareLink(share, true)
              : undefined}
          />
        ))}

        {/* Shared games (received) */}
        {(section === 'shared_public' || section === 'shared_private') && displaySharedGames.map(game => (
          <SharedGameCard
            key={game.id}
            game={game}
            pseudo={pseudo}
            highlighted={highlightedGameId === game.id}
            isAdmin={isAdmin}
            onPoll={game.is_poll && !game.user_has_voted ? () => handlePollVote(game) : undefined}
            onShowResults={game.is_poll && game.user_has_voted ? () => setPollResultsGame(game) : undefined}
            onContinue={game.is_poll && game.user_has_voted ? () => handleOpenSharedGame(game.id, 'play') : undefined}
            onReplayFromStart={game.is_poll && game.user_has_voted ? () => handleOpenSharedGameFromStart(game.id) : undefined}
            onReview={() => handleOpenSharedGame(game.id, 'replay')}
            onReplay={() => handleOpenSharedGame(game.id, 'play')}
            onCopyLink={game.allow_reshare ? () => handleCopyShareLink(game) : undefined}
            onOpenDiffere={canOpenDiffereFromLibrary(game)
              ? () => handleOpenDiffereSource(game)
              : undefined}
            onAdminDelete={isAdmin ? () => handleAdminDeleteShared(game.id) : undefined}
            onAdminUpdate={isAdmin ? (u) => handleAdminUpdateShared(game.id, u) : undefined}
          />
        ))}

        {hasMore && gameCount > 0 && (
          <div ref={sentinelRef} className="library-load-sentinel" aria-hidden="true" />
        )}
        {loadingMore && (
          <div className="library-loading-more">Chargement…</div>
        )}
      </div>

      {shareGame !== null && (
        <ShareGameModal
          pseudo={pseudo}
          gameId={shareGame.id}
          gameTags={shareGame.tags}
          gameNote={shareGame.note}
          onClose={() => setShareGame(null)}
          onShared={() => {
            setSection('my_shares');
            if (isCompact) setShowMobileDetail(true);
            fetchTagCounts();
            void refreshSection();
          }}
        />
      )}

      {shareLinkModal !== null && (
        <ShareLinkModal
          sharedGameId={shareLinkModal.id}
          shareType={shareLinkModal.shareType}
          allowReshare={shareLinkModal.allowReshare}
          isOwner={shareLinkModal.isOwner}
          onClose={() => setShareLinkModal(null)}
        />
      )}

      {pollResultsGame !== null && (
        <PollModal
          pseudo={pseudo}
          sharedGame={pollResultsGame}
          onClose={() => setPollResultsGame(null)}
        />
      )}
    </div>
  );

  return (
    <div className="library-container">
      <MasterDetail
        master={sidebar}
        detail={mainContent}
        detailTitle={detailTitle}
        hasSelection={showMobileDetail}
        onBack={() => setShowMobileDetail(false)}
      />
    </div>
  );
}

// ============================================
// Helper: determine game type for actions
// ============================================

type GameKind = 'complete' | 'partial' | 'distribution' | 'poll';

function getPartialLabel(partialPhase?: number, partialTrickNum?: number): string {
  if (partialPhase === 1 || partialPhase === undefined) return 'Enchères';
  if (partialPhase === 2) return 'Écart';
  if (partialTrickNum && partialTrickNum > 0) return `Pli ${partialTrickNum}`;
  return 'En jeu';
}

function getGameKind(game: LibraryGameSummary): GameKind {
  if (!game.is_partial) return 'complete';
  if (game.partial_phase !== undefined && game.partial_phase !== null) {
    if (game.partial_phase <= 1 && (!game.partial_trick_num || game.partial_trick_num === 0)) {
      return 'distribution';
    }
  }
  return 'partial';
}

function getSharedGameKind(game: LibrarySharedGameSummary): GameKind {
  if (game.is_poll) return 'poll';
  if (!game.is_partial) return 'complete';
  if (game.partial_phase !== undefined && game.partial_phase !== null) {
    if (game.partial_phase <= 1 && (!game.partial_trick_num || game.partial_trick_num === 0)) {
      return 'distribution';
    }
  }
  return 'partial';
}

function getShareKind(share: LibraryShareInfo): GameKind {
  if (share.is_poll) return 'poll';
  if (!share.is_partial) return 'complete';
  return 'partial';
}

// ============================================
// OwnGameCard
// ============================================

function LibraryCommentsPreview({
  count,
  lastComment,
  lastAuthor,
}: {
  count?: number;
  lastComment?: string | null;
  lastAuthor?: string | null;
}) {
  if (!count || count <= 0) return null;
  const preview = lastComment
    ? `${lastComment.slice(0, 80)}${lastComment.length > 80 ? '…' : ''}`
    : null;

  return (
    <div className="library-game-card-comments">
      <span className="library-comment-count">
        {count} commentaire{count > 1 ? 's' : ''}
      </span>
      {preview && (
        <span className="library-comment-preview">
          {lastAuthor ? `${lastAuthor} : ` : ''}{preview}
        </span>
      )}
    </div>
  );
}

function OwnGameCard({
  game,
  customTags,
  editingTags,
  editingNote,
  onEditTags,
  onEditNote,
  onSaveTags,
  onSaveNote,
  onCancelEditTags,
  onCancelEditNote,
  onDelete,
  onReview,
  onReplay,
  onShare,
  onOpenDiffere,
  highlighted = false,
}: {
  game: LibraryGameSummary;
  highlighted?: boolean;
  customTags: string[];
  editingTags: boolean;
  editingNote: boolean;
  onEditTags: () => void;
  onEditNote: () => void;
  onSaveTags: (tags: string[]) => void;
  onSaveNote: (note: string) => void;
  onCancelEditTags: () => void;
  onCancelEditNote: () => void;
  onDelete: () => void;
  onReview: () => void;
  onReplay: () => void;
  onShare: () => void;
  onOpenDiffere?: () => void;
}) {
  const isPending = game.differe_pending;
  const kind = getGameKind(game);
  const cardTitle = formatLibraryDiffereCardTitle(game, ORIGIN_LABELS);
  const date = new Date(game.created_at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });

  return (
    <div
      className={`library-game-card ${isPending ? 'pending' : ''}${highlighted ? ' library-game-card--highlighted' : ''}`}
      data-own-game-id={game.id}
    >
      <GameAvatar kind={kind} />

      <div className="library-game-card-info">
        <div className="library-game-card-title">
          {cardTitle}
          {game.contract !== undefined && game.contract !== null && (
            <> · {CONTRACT_LABELS[game.contract] ?? '?'}</>
          )}
          {(kind === 'partial' || kind === 'distribution') && (
            <span className="library-partial-label">
              {kind === 'distribution' ? 'Distribution' : getPartialLabel(game.partial_phase, game.partial_trick_num)}
            </span>
          )}
        </div>
        <div className="library-game-card-meta">
          {date} · {game.player_count}j
          {!editingNote && game.note && (
            <> · <span className="library-game-card-note">{game.note.slice(0, 40)}{game.note.length > 40 ? '…' : ''}</span></>
          )}
          {!editingNote && (
            <button
              type="button"
              className="library-note-edit-btn"
              onClick={e => { e.stopPropagation(); onEditNote(); }}
              title={game.note ? 'Modifier la note' : 'Ajouter une note'}
            >
              <EditIcon size={10} />
            </button>
          )}
        </div>
        {editingNote && (
          <NoteEditor
            currentNote={game.note}
            onSave={onSaveNote}
            onCancel={onCancelEditNote}
          />
        )}
        <LibraryCommentsPreview
          count={game.comment_count}
          lastComment={game.last_comment}
          lastAuthor={game.last_comment_author}
        />
        <div className="library-game-card-tags">
          {game.tags.map(tag => (
            <span key={tag} className="library-game-card-tag" data-tag={tag}>{tag}</span>
          ))}
          <button
            className="library-game-card-tag"
            style={{ cursor: 'pointer', opacity: 0.6, border: '1px dashed var(--border-color)' }}
            onClick={e => { e.stopPropagation(); onEditTags(); }}
            title="Modifier les tags"
          >
            <EditIcon size={10} />
          </button>
        </div>

        {editingTags && (
          <TagEditor
            currentTags={game.tags}
            customTags={customTags}
            onSave={onSaveTags}
            onCancel={onCancelEditTags}
          />
        )}
      </div>

      {isPending ? (
        <span className="library-pending-badge">En attente</span>
      ) : (
        <div className="library-game-card-actions">
          {kind === 'complete' && (
            <>
              <button className="action-review" title="Revoir la partie" onClick={e => { e.stopPropagation(); onReview(); }}>
                <EyeIcon size={13} /> Revoir
              </button>
              <button className="action-replay" title="Rejouer la partie" onClick={e => { e.stopPropagation(); onReplay(); }}>
                <ReplayIcon size={13} /> Rejouer
              </button>
            </>
          )}
          {kind === 'partial' && (
            <>
              <button className="action-continue" title="Continuer la partie" onClick={e => { e.stopPropagation(); onReplay(); }}>
                <ContinueIcon size={13} /> Continuer
              </button>
              <button className="action-replay" title="Rejouer depuis le début" onClick={e => { e.stopPropagation(); onReview(); }}>
                <ReplayIcon size={13} /> Rejouer
              </button>
            </>
          )}
          {kind === 'distribution' && (
            <button className="action-play" title="Jouer cette distribution" onClick={e => { e.stopPropagation(); onReplay(); }}>
              <PlayIcon size={13} /> Jouer
            </button>
          )}
          <button className="action-share" title="Partager" onClick={e => { e.stopPropagation(); onShare(); }}>
            <ShareIcon size={13} />
          </button>
          {onOpenDiffere && (
            <button
              className="action-differe-link"
              title="Voir cette donne dans les résultats différé"
              onClick={e => { e.stopPropagation(); onOpenDiffere(); }}
            >
              ↩
            </button>
          )}
          <button title="Supprimer" className="danger" onClick={e => { e.stopPropagation(); onDelete(); }}>
            <TrashIcon size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// SharedGameCard
// ============================================

function SharedGameCard({
  game,
  pseudo,
  highlighted = false,
  isAdmin,
  onPoll,
  onShowResults,
  onContinue,
  onReplayFromStart,
  onReview,
  onReplay,
  onCopyLink,
  onOpenDiffere,
  onAdminDelete,
  onAdminUpdate,
}: {
  game: LibrarySharedGameSummary;
  pseudo: string;
  highlighted?: boolean;
  isAdmin?: boolean;
  onPoll?: () => void;
  onShowResults?: () => void;
  onContinue?: () => void;
  onReplayFromStart?: () => void;
  onReview: () => void;
  onReplay: () => void;
  onCopyLink?: () => void;
  onOpenDiffere?: () => void;
  onAdminDelete?: () => void;
  onAdminUpdate?: (u: { tags?: string[]; is_poll?: boolean; share_comment?: string | null }) => void;
}) {
  const [adminEditing, setAdminEditing] = useState(false);
  const [editComment, setEditComment] = useState(game.author_comment ?? '');
  const [editPoll, setEditPoll] = useState(game.is_poll);
  const [editTags, setEditTags] = useState<string[]>(game.tags ?? []);

  const kind = getSharedGameKind(game);
  const cardTitle = formatLibraryDiffereCardTitle(game, ORIGIN_LABELS);
  const date = new Date(game.shared_at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });

  const handleAdminSave = () => {
    const updates: { tags?: string[]; is_poll?: boolean; share_comment?: string | null } = {};
    const tagsChanged = JSON.stringify(editTags) !== JSON.stringify(game.tags ?? []);
    if (tagsChanged) updates.tags = editTags;
    if (editPoll !== game.is_poll) updates.is_poll = editPoll;
    const commentChanged = editComment.trim() !== (game.author_comment ?? '').trim();
    if (commentChanged) updates.share_comment = editComment.trim() || null;
    if (Object.keys(updates).length > 0) onAdminUpdate?.(updates);
    setAdminEditing(false);
  };

  return (
    <div
      className={`library-game-card${highlighted ? ' library-game-card--highlighted' : ''}${game.is_new ? ' library-game-card--unread' : ''}`}
      data-shared-game-id={game.id}
    >
      <GameAvatar kind={kind} />

      <div className="library-game-card-info">
        <div className="library-game-card-title">
          {cardTitle}
          {game.contract !== undefined && game.contract !== null && (
            <> · {CONTRACT_LABELS[game.contract] ?? '?'}</>
          )}
          {game.is_new && (
            <span className="library-unread-badge">Nouveau</span>
          )}
          {game.is_poll && (
            <span className="library-poll-badge" style={{ marginLeft: 6 }}>
              Sondage{game.poll_response_count ? ` (${game.poll_response_count})` : ''}
            </span>
          )}
          {game.is_poll && (game.poll_response_count ?? 0) === 0 && (
            <span className="library-poll-pending-badge">En attente de votes</span>
          )}
          {!game.is_poll && (kind === 'partial' || kind === 'distribution') && (
            <span className="library-partial-label">
              {kind === 'distribution' ? 'Distribution' : getPartialLabel(game.partial_phase, game.partial_trick_num)}
            </span>
          )}
        </div>
        <div className="library-game-card-meta">
          {date} · {game.player_count}j ·{' '}
          <span className="library-game-card-author">
            <PlayerAvatar pseudo={game.shared_by} size={14} />
            {game.shared_by}
          </span>
        </div>
        {game.author_comment && !adminEditing && (
          <div className="library-game-card-comment library-game-card-share-message">
            {game.author_comment.slice(0, 80)}{game.author_comment.length > 80 ? '…' : ''}
          </div>
        )}
        {game.is_poll && !game.user_has_voted && onPoll && (
          <div className="library-poll-card-hint">
            Répondez au sondage pour indiquer votre choix à ce moment de la partie
          </div>
        )}
        {!adminEditing && (
          <LibraryCommentsPreview
            count={game.comment_count}
            lastComment={game.last_comment}
            lastAuthor={game.last_comment_author}
          />
        )}
        {game.is_poll && (game.poll_response_count ?? 0) > 0 && game.user_has_voted && (
          <PollResultsPreview
            pseudo={pseudo}
            sharedGameId={game.id}
            voteCount={game.poll_response_count ?? 0}
            onOpenResults={onShowResults}
          />
        )}
        {!adminEditing && game.tags && game.tags.length > 0 && (
          <div className="library-game-card-tags">
            {game.tags.map(tag => (
              <span key={tag} className="library-game-card-tag" data-tag={tag}>{tag}</span>
            ))}
          </div>
        )}

        {/* Admin inline editor */}
        {adminEditing && (
          <div className="library-admin-editor" onClick={e => e.stopPropagation()}>
            <div className="library-admin-editor-row">
              <label className="library-share-option" style={{ fontSize: '0.78rem' }}>
                <input type="checkbox" checked={editPoll} onChange={e => setEditPoll(e.target.checked)} />
                Sondage
              </label>
            </div>
            <div className="library-admin-editor-row">
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 3 }}>Tags</div>
              <div className="library-tag-editor-chips">
                {[...PREDEFINED_TAGS, ...AUTO_TAGS].map(tag => {
                  const isSelected = editTags.includes(tag);
                  return (
                    <span
                      key={tag}
                      className={`library-tag-editor-chip ${isSelected ? 'selected' : ''}`}
                      data-tag={tag}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setEditTags(prev =>
                        isSelected ? prev.filter(t => t !== tag) : [...prev, tag],
                      )}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="library-admin-editor-row">
              <textarea
                className="library-modal-textarea"
                rows={2}
                value={editComment}
                onChange={e => setEditComment(e.target.value)}
                placeholder="Commentaire…"
                style={{ fontSize: '0.78rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <button className="library-modal-btn secondary" style={{ padding: '3px 10px', fontSize: '0.72rem' }} onClick={() => setAdminEditing(false)}>
                Annuler
              </button>
              <button className="library-modal-btn primary" style={{ padding: '3px 10px', fontSize: '0.72rem' }} onClick={handleAdminSave}>
                Enregistrer
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="library-game-card-actions">
        {kind === 'poll' && (
          game.user_has_voted ? (
            <>
              {onShowResults && (
                <button className="action-review" title="Voir les résultats" onClick={e => { e.stopPropagation(); onShowResults(); }}>
                  <EyeIcon size={13} /> Résultats
                </button>
              )}
              {onContinue && (
                <button className="action-continue" title="Continuer la partie" onClick={e => { e.stopPropagation(); onContinue(); }}>
                  <ContinueIcon size={13} /> Continuer
                </button>
              )}
              {onReplayFromStart && (
                <button className="action-replay" title="Rejouer depuis le début" onClick={e => { e.stopPropagation(); onReplayFromStart(); }}>
                  <ReplayIcon size={13} /> Rejouer
                </button>
              )}
            </>
          ) : onPoll ? (
            <button className="action-poll" title="Répondre au sondage" onClick={e => { e.stopPropagation(); onPoll(); }}>
              <PollIcon size={13} /> Répondre au sondage
            </button>
          ) : null
        )}
        {kind === 'complete' && (
          <>
            <button className="action-review" title="Revoir" onClick={e => { e.stopPropagation(); onReview(); }}>
              <EyeIcon size={13} /> Revoir
            </button>
            <button className="action-replay" title="Rejouer" onClick={e => { e.stopPropagation(); onReplay(); }}>
              <ReplayIcon size={13} /> Rejouer
            </button>
          </>
        )}
        {kind === 'partial' && (
          <>
            <button className="action-continue" title="Continuer" onClick={e => { e.stopPropagation(); onReplay(); }}>
              <ContinueIcon size={13} /> Continuer
            </button>
            <button className="action-replay" title="Rejouer" onClick={e => { e.stopPropagation(); onReview(); }}>
              <ReplayIcon size={13} /> Rejouer
            </button>
          </>
        )}
        {kind === 'distribution' && (
          <button className="action-play" title="Jouer" onClick={e => { e.stopPropagation(); onReplay(); }}>
            <PlayIcon size={13} /> Jouer
          </button>
        )}
        {onCopyLink && (
          <button className="action-copy-link" title="Copier le lien" onClick={e => { e.stopPropagation(); onCopyLink(); }}>
            <LinkIcon size={13} />
          </button>
        )}
        {onOpenDiffere && (
          <button
            className="action-differe-link"
            title="Voir cette donne dans les résultats différé"
            onClick={e => { e.stopPropagation(); onOpenDiffere(); }}
          >
            ↩
          </button>
        )}
        {isAdmin && onAdminUpdate && !adminEditing && (
          <button title="Modifier (admin)" onClick={e => { e.stopPropagation(); setAdminEditing(true); }}>
            <EditIcon size={13} />
          </button>
        )}
        {isAdmin && onAdminDelete && (
          <button title="Supprimer (admin)" className="danger" onClick={e => { e.stopPropagation(); onAdminDelete(); }}>
            <TrashIcon size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================
// GameAvatar — status icon with color
// ============================================

function GameAvatar({ kind }: { kind: GameKind }) {
  if (kind === 'poll') {
    return (
      <div className="library-game-avatar poll">
        <PollIcon size={20} />
      </div>
    );
  }
  if (kind === 'partial' || kind === 'distribution') {
    return (
      <div className="library-game-avatar partial">
        <GamePartialIcon size={20} />
      </div>
    );
  }
  return (
    <div className="library-game-avatar complete">
      <GameCompleteIcon size={20} />
    </div>
  );
}

// ============================================
// MyShareCard — outgoing shares
// ============================================

function MyShareCard({
  share,
  pseudo,
  highlighted = false,
  onReview,
  onShowResults,
  onCancel,
  onCopyLink,
}: {
  share: LibraryShareInfo;
  pseudo: string;
  highlighted?: boolean;
  onReview: () => void;
  onShowResults?: () => void;
  onCancel: () => void;
  onCopyLink?: () => void;
}) {
  const kind = getShareKind(share);
  const originLabel = ORIGIN_LABELS[share.origin_type] ?? share.origin_type;
  const date = new Date(share.shared_at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  });
  const isPublic = share.share_type === 'public';
  const recipients = share.recipients ?? [];

  return (
    <div
      className={`library-game-card${highlighted ? ' library-game-card--highlighted' : ''}`}
      data-shared-game-id={share.id}
    >
      <GameAvatar kind={kind} />

      <div className="library-game-card-info">
        <div className="library-game-card-title">
          {originLabel}
          {share.contract !== undefined && share.contract !== null && (
            <> — {CONTRACT_LABELS[share.contract] ?? '?'}</>
          )}
          {share.is_poll && (
            <span className="library-poll-badge" style={{ marginLeft: 6 }}>
              Sondage{share.poll_response_count ? ` (${share.poll_response_count})` : ''}
            </span>
          )}
          {share.is_poll && share.poll_response_count === 0 && (
            <span className="library-poll-pending-badge">En attente de votes</span>
          )}
          {share.is_partial && !share.is_poll && (
            <span className="library-partial-label">Partielle</span>
          )}
        </div>
        <div className="library-game-card-meta">
          {date} · {share.player_count}j
          {share.source_game_id ? ` · Partie #${share.source_game_id}` : ''}
        </div>
        <div className="library-share-visibility">
          {isPublic ? (
            <span className="library-share-visibility-badge public">
              <GlobeIcon size={11} /> Publique
            </span>
          ) : (
            <span className="library-share-visibility-badge private">
              <LockIcon size={11} /> Privée
              {recipients.length > 0 && (
                <> · {recipients.length} destinataire{recipients.length !== 1 ? 's' : ''}</>
              )}
            </span>
          )}
        </div>
        {!isPublic && recipients.length > 0 && (
          <div className="library-share-recipients">
            {recipients.map(r => (
              <span key={`${r.type}:${r.id}`} className="library-share-recipient-chip">
                {r.type === 'team' ? <GroupIcon size={10} /> : null}
                {r.type === 'team' ? `Équipe ${r.id}` : r.id}
              </span>
            ))}
          </div>
        )}
        {share.tags && share.tags.length > 0 && (
          <div className="library-game-card-tags">
            {share.tags.map(tag => (
              <span key={tag} className="library-game-card-tag" data-tag={tag}>{tag}</span>
            ))}
          </div>
        )}
        <LibraryCommentsPreview
          count={share.comment_count}
          lastComment={share.last_comment}
          lastAuthor={share.last_comment_author}
        />
        {share.is_poll && share.poll_response_count > 0 && (
          <PollResultsPreview
            pseudo={pseudo}
            sharedGameId={share.id}
            voteCount={share.poll_response_count}
            onOpenResults={onShowResults}
          />
        )}
      </div>

      <div className="library-game-card-actions">
        <button className="action-review" title="Revoir la copie partagée" onClick={e => { e.stopPropagation(); onReview(); }}>
          <EyeIcon size={13} /> Revoir
        </button>
        {onShowResults && (
          <button className="action-review" title="Voir les résultats du sondage" onClick={e => { e.stopPropagation(); onShowResults(); }}>
            <PollIcon size={13} /> Résultats
          </button>
        )}
        {onCopyLink && (
          <button className="action-copy-link" title="Copier le lien" onClick={e => { e.stopPropagation(); onCopyLink(); }}>
            <LinkIcon size={13} />
          </button>
        )}
        <button title="Annuler le partage" className="danger" onClick={e => { e.stopPropagation(); onCancel(); }}>
          <TrashIcon size={13} /> Annuler
        </button>
      </div>
    </div>
  );
}

// ============================================
// NoteEditor — inline note editing
// ============================================

function NoteEditor({
  currentNote,
  onSave,
  onCancel,
}: {
  currentNote?: string;
  onSave: (note: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(currentNote ?? '');

  return (
    <div className="library-note-editor" onClick={e => e.stopPropagation()}>
      <textarea
        className="library-note-editor-input"
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Note personnelle…"
        rows={2}
        maxLength={500}
        autoFocus
      />
      <div className="library-note-editor-actions">
        <button type="button" className="library-note-editor-cancel" onClick={onCancel}>
          Annuler
        </button>
        <button type="button" className="library-note-editor-save" onClick={() => onSave(text)}>
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ============================================
// TagEditor — inline tag editing popover
// ============================================

function TagEditor({
  currentTags,
  customTags,
  onSave,
  onCancel,
}: {
  currentTags: string[];
  customTags: string[];
  onSave: (tags: string[]) => void;
  onCancel: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(currentTags));
  const [newTag, setNewTag] = useState('');

  const autoTags = currentTags.filter(t => (AUTO_TAGS as unknown as string[]).includes(t));

  const toggleChip = (tag: string) => {
    if ((AUTO_TAGS as unknown as string[]).includes(tag)) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const handleAddCustom = () => {
    const trimmed = newTag.trim().toLowerCase();
    if (!trimmed) return;
    setSelected(prev => new Set(prev).add(trimmed));
    setNewTag('');
  };

  const handleSave = () => {
    const tags = [...selected];
    for (const at of autoTags) {
      if (!tags.includes(at)) tags.push(at);
    }
    onSave(tags);
  };

  return (
    <div className="library-tag-editor" style={{ position: 'relative', marginTop: 8 }} onClick={e => e.stopPropagation()}>
      <div className="library-tag-editor-title">
        <TagIcon size={12} /> Modifier les tags
      </div>

      {/* Auto tags (read-only) */}
      {autoTags.length > 0 && (
        <div className="library-tag-editor-group">
          <div className="library-tag-editor-group-label">Automatiques</div>
          <div className="library-tag-editor-chips">
            {autoTags.map(tag => (
              <span key={tag} className="library-tag-editor-chip auto-tag" data-tag={tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Predefined */}
      <div className="library-tag-editor-group">
        <div className="library-tag-editor-group-label">Prédéfinis</div>
        <div className="library-tag-editor-chips">
          {(PREDEFINED_TAGS as unknown as string[]).map(tag => (
            <button
              key={tag}
              className={`library-tag-editor-chip ${selected.has(tag) ? 'selected' : ''}`}
              data-tag={tag}
              onClick={() => toggleChip(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Custom */}
      {customTags.length > 0 && (
        <div className="library-tag-editor-group">
          <div className="library-tag-editor-group-label">Personnalisés</div>
          <div className="library-tag-editor-chips">
            {customTags.map(tag => (
              <button
                key={tag}
                className={`library-tag-editor-chip ${selected.has(tag) ? 'selected' : ''}`}
                data-tag={tag}
                onClick={() => toggleChip(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* New custom tag */}
      <div className="library-tag-editor-custom-input">
        <input
          value={newTag}
          onChange={e => setNewTag(e.target.value)}
          placeholder="Nouveau tag…"
          onKeyDown={e => { if (e.key === 'Enter') handleAddCustom(); }}
        />
        <button onClick={handleAddCustom}>+</button>
      </div>

      <div className="library-tag-editor-save">
        <button onClick={onCancel} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', marginRight: 6 }}>
          Annuler
        </button>
        <button onClick={handleSave}>Enregistrer</button>
      </div>
    </div>
  );
}
